// AI-1277 — Evidência do dispositivo para o aceite automático do termo da Soma.
// É a prova de QUEM autorizou e DE ONDE: só vai dado real do navegador. Campo não
// obtido é null, e nunca há coordenada fixa ou inventada. A coleta nunca trava nem
// quebra a consulta: qualquer falha faz o /consult seguir sem `dispositivo`.

// permissao_pendente: o operador ainda não decidiu o prompt (é pedido só na home), e o
// fluxo de consulta não abre prompt para não interromper o operador.
type GeoErro = 'permissao_negada' | 'permissao_pendente' | 'indisponivel' | 'timeout';

export type Geo =
    | { ok: true; lat: number; lng: number; precisao: number | null; marca_tempo: number; fonte: 'navegador' | 'google_ip' }
    | { ok: false; erro: GeoErro };

export type Dispositivo = {
    modelo_dispositivo: string | null;
    plataforma: string | null;
    // AI-1301 — Presença (deviceType). Só o que o navegador informa; nunca deduzido do user agent.
    tipo_dispositivo: 'mobile' | 'desktop' | null;
    idioma: string | null;
    fuso_horario: string | null;
    resolucao_tela: string | null;
    geolocalizacao: Geo;
};

const GEO_ERROS: Record<number, GeoErro> = { 1: 'permissao_negada', 2: 'indisponivel', 3: 'timeout' };
// No clique, 3s do navegador + 2s do Google cabem no prazo de 5s.
const TIMEOUT_NAVEGADOR_CLIQUE_MS = 3000;
// Leitura adiantada (home, abertura da consulta, renovação): ninguém está esperando.
const TIMEOUT_NAVEGADOR_ADIANTADO_MS = 15000;
const TIMEOUT_GOOGLE_MS = 2000;
const PRAZO_CLIQUE_MS = 5000;
const VALIDADE_CACHE_MS = 5 * 60 * 1000;
// O navegador devolve na hora uma leitura de até 5 min em vez de começar do zero.
// Continua sendo evidência verdadeira: marca_tempo é a hora real da leitura.
const IDADE_MAXIMA_LEITURA_MS = VALIDADE_CACHE_MS;
// Menor que a validade do cache, para ele não vencer entre consultas seguidas.
const INTERVALO_RENOVACAO_MS = 4 * 60 * 1000;

const numeroFinito = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const texto = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

const comPrazo = <T>(p: Promise<T>, ms: number, aoEstourar: T): Promise<T> =>
    Promise.race([p, new Promise<T>((resolve) => setTimeout(() => resolve(aoEstourar), ms))]);

// ─── Geolocalização ───────────────────────────────────────────────────────────

// Dispara o prompt "Local" do navegador quando a permissão ainda não foi decidida —
// por isso só é chamado direto na home, ou na consulta quando já está permitido.
// O timeout do navegador só conta depois que o operador responde ao prompt: o prazo
// do clique é garantido por comPrazo, não por ele.
const geoNavegador = (timeoutMs: number): Promise<Geo> =>
    new Promise((resolve) => {
        if (!window.isSecureContext || !navigator.geolocation) {
            resolve({ ok: false, erro: 'indisponivel' });
            return;
        }
        navigator.geolocation.getCurrentPosition(
            ({ coords, timestamp }) => {
                if (!numeroFinito(coords.latitude) || !numeroFinito(coords.longitude)) {
                    resolve({ ok: false, erro: 'indisponivel' });
                    return;
                }
                resolve({
                    ok: true,
                    lat: coords.latitude,
                    lng: coords.longitude,
                    precisao: numeroFinito(coords.accuracy) ? coords.accuracy : null,
                    // O back exige inteiro: decimal aqui derruba o /consult com 422.
                    marca_tempo: Math.round(timestamp),
                    fonte: 'navegador',
                });
            },
            (e) => resolve({ ok: false, erro: GEO_ERROS[e.code] ?? 'indisponivel' }),
            // Operador em desktop não tem GPS: alta precisão só aumenta a chance de timeout.
            { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: IDADE_MAXIMA_LEITURA_MS },
        );
    });

// Fallback aproximado pelo IP. Precisa sair do navegador: do back, o Google
// localizaria o servidor, e não o operador. fetch puro de propósito — a instância
// `api` levaria baseURL e credenciais do Beviajuda para o Google.
const geoGoogle = async (): Promise<Geo | null> => {
    const key = process.env.REACT_APP_GOOGLE_GEOLOCATION_KEY;
    if (!key) return null;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), TIMEOUT_GOOGLE_MS);
    try {
        const r = await fetch(`https://www.googleapis.com/geolocation/v1/geolocate?key=${encodeURIComponent(key)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ considerIp: true }),
            signal: ctrl.signal,
        });
        if (!r.ok) return null;
        const j = await r.json();
        const lat = j?.location?.lat;
        const lng = j?.location?.lng;
        if (!numeroFinito(lat) || !numeroFinito(lng)) return null;
        return {
            ok: true,
            lat,
            lng,
            // accuracy real do Google (milhares de metros): é a informação honesta de
            // que a posição é aproximada, nunca "melhorar".
            precisao: numeroFinito(j.accuracy) ? j.accuracy : null,
            marca_tempo: Date.now(),
            fonte: 'google_ip',
        };
    } catch {
        return null;
    } finally {
        clearTimeout(timer);
    }
};

let ultimoOk: { geo: Geo; em: number } | null = null;
let emAndamento: Promise<Geo> | null = null;

const cacheValido = (): Geo | null =>
    ultimoOk && Date.now() - ultimoOk.em < VALIDADE_CACHE_MS ? ultimoOk.geo : null;

const guardar = (geo: Geo): Geo => {
    // Só posição obtida é guardada: falha é tentada de novo na próxima consulta.
    if (geo.ok) ultimoOk = { geo, em: Date.now() };
    return geo;
};

// null = navegador sem Permissions API para geolocalização: estado desconhecido.
const estadoPermissao = async (): Promise<PermissionState | null> => {
    try {
        if (!navigator.permissions?.query) return null;
        return (await navigator.permissions.query({ name: 'geolocation' })).state;
    } catch {
        return null;
    }
};

/**
 * Chamar na home: abre o prompt "Local" do navegador se o operador ainda não
 * decidiu, e já guarda a posição quando ele permite. Nunca rejeita.
 */
export const solicitarPermissaoLocalizacao = (): void => {
    if (cacheValido()) return;
    geoNavegador(TIMEOUT_NAVEGADOR_ADIANTADO_MS).then(guardar).catch(() => undefined);
};

/**
 * Localização para a consulta. NUNCA abre o prompt de permissão: só usa o navegador
 * se já estiver permitido; senão vai direto ao fallback do Google. Nunca rejeita.
 * O padrão é a leitura adiantada (timeout longo); o clique passa o timeout curto.
 * Se já houver uma leitura em andamento, ela é reaproveitada.
 */
export const iniciarGeolocalizacao = (timeoutNavegadorMs: number = TIMEOUT_NAVEGADOR_ADIANTADO_MS): Promise<Geo> => {
    const cache = cacheValido();
    if (cache) return Promise.resolve(cache);
    if (!emAndamento) {
        emAndamento = (async (): Promise<Geo> => {
            const estado = await estadoPermissao();
            let nav: Geo;
            if (estado === 'granted') nav = await geoNavegador(timeoutNavegadorMs);
            else if (estado === 'denied') nav = { ok: false, erro: 'permissao_negada' };
            // 'prompt' ou desconhecido: chamar o navegador abriria o prompt no meio da consulta.
            else nav = { ok: false, erro: 'permissao_pendente' };
            // Sem Google (ou com falha dele), vai o motivo real da falha do navegador.
            return guardar(nav.ok ? nav : (await geoGoogle()) ?? nav);
        })()
            .catch((): Geo => ({ ok: false, erro: 'indisponivel' }))
            .finally(() => { emAndamento = null; });
    }
    return emAndamento;
};

/**
 * Renova a posição a cada 4 min enquanto a tela de consulta está aberta, para o
 * cache não vencer entre consultas seguidas. Só usa o navegador já permitido e com
 * a aba visível: não abre prompt e não gasta chamadas do Google em segundo plano.
 * Uma renovação que falha mantém a posição anterior. Retorna a função de parar.
 */
export const manterGeolocalizacaoAtualizada = (): (() => void) => {
    const id = window.setInterval(async () => {
        try {
            if (document.hidden || (await estadoPermissao()) !== 'granted') return;
            guardar(await geoNavegador(TIMEOUT_NAVEGADOR_ADIANTADO_MS));
        } catch { /* mantém a posição anterior */ }
    }, INTERVALO_RENOVACAO_MS);
    return () => window.clearInterval(id);
};

// ─── Dispositivo ──────────────────────────────────────────────────────────────

const modeloDispositivo = async (uad: any): Promise<string | null> => {
    try {
        const valores = await comPrazo<any>(uad?.getHighEntropyValues?.(['model']) ?? Promise.resolve(null), 500, null);
        // Desktop devolve "" → null.
        return texto(valores?.model);
    } catch {
        return null;
    }
};

const PLATAFORMAS_DESKTOP = ['Windows', 'macOS', 'Linux', 'Chrome OS'];

// userAgentData.mobile existe só em Chrome/Edge. false não distingue tablet de desktop, então
// "desktop" só vale quando a plataforma informada pelo navegador também é de desktop.
export const tipoDispositivo = (uad: any): 'mobile' | 'desktop' | null => {
    if (typeof uad?.mobile !== 'boolean') return null;
    if (uad.mobile) return 'mobile';
    return PLATAFORMAS_DESKTOP.includes(uad.platform) ? 'desktop' : null;
};

const resolucaoTela = (): string | null => {
    const w = window.screen?.width;
    const h = window.screen?.height;
    return numeroFinito(w) && numeroFinito(h) && w > 0 && h > 0 ? `${w}x${h}` : null;
};

const fusoHorario = (): string | null => {
    try {
        return texto(Intl.DateTimeFormat().resolvedOptions().timeZone);
    } catch {
        return null;
    }
};

/**
 * Evidência para o body do /consult. Espera a localização no máximo PRAZO_CLIQUE_MS.
 * Retorna undefined em qualquer falha: a consulta segue sem `dispositivo`.
 */
export const coletarDispositivo = async (): Promise<Dispositivo | undefined> => {
    try {
        const uad = (navigator as any).userAgentData;
        const [geolocalizacao, modelo] = await Promise.all([
            comPrazo<Geo>(iniciarGeolocalizacao(TIMEOUT_NAVEGADOR_CLIQUE_MS), PRAZO_CLIQUE_MS, { ok: false, erro: 'timeout' }),
            modeloDispositivo(uad),
        ]);
        return {
            modelo_dispositivo: modelo,
            // Firefox/Safari não têm userAgentData: navigator.platform também é dado real.
            plataforma: texto(uad?.platform) ?? texto(navigator.platform),
            tipo_dispositivo: tipoDispositivo(uad),
            idioma: texto(navigator.language),
            fuso_horario: fusoHorario(),
            resolucao_tela: resolucaoTela(),
            geolocalizacao,
        };
    } catch {
        return undefined;
    }
};
